import {t, Trans} from '@lingui/macro';
import React, {FC, useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {TextBox} from '../../../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {fontSizes, fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useLocalize} from '../../../../../../../srcCommon/internationalization/internationalizationContext';
import {reportToBugsnag} from '../../../../../../../srcCommon/services/bugsnag/bugsnag';
import {ApiSearchTopic} from '../../../../../../models/api/searchTopicModel';
import {TopicModel} from '../../../../../../models/api/topicModel';
import {fetchApiAsync} from '../../../../../../services/http/http';
import {HttpError} from '../../../../../../services/http/httpError';
import {OkCancelDialog} from '../../../../../common/dialogs/okCancelDialog';

/*
 * Props.
 */

// TODO: Refactor into two components so we can enforce props for adding and editing separately.
interface AddOrEditTopicDialogProps {
  createAndAddTopic?: (link: string, name: string) => void;
  updateTopicName: (topicId: number, name: string) => void;
  companyUrl: string;
  onRequestClose?: Handler;
  topicToEdit?: TopicModel;
}

/*
 * Style.
 */

const StyledExplanatoryTextDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
  margin: 6px 0px 12px 0px;
  /* Need to override styling from Paragraph in DialogContent.tsx. */
  line-height: 18px !important;
`;

/*
 * Component.
 */

export const AddOrEditTopicDialog: FC<AddOrEditTopicDialogProps> = (props) => {
  const {createAndAddTopic, updateTopicName, onRequestClose, companyUrl, topicToEdit} = props;

  const localize = useLocalize();

  const [topicName, setTopicName] = useState(topicToEdit ? topicToEdit.name : '');
  const [topicUrl, setTopicUrl] = useState(topicToEdit ? topicToEdit.externalLink : '');
  const [matchingTopic, setMatchingTopic] = useState<TopicModel>();
  const hadMatchingTopicRef = useRef(false);

  const onOkClick = () => {
    if (topicToEdit) {
      updateTopicName(topicToEdit.id, topicName);
    } else if (createAndAddTopic) {
      createAndAddTopic(topicUrl, topicName);
    }
    return onRequestClose && onRequestClose();
  };

  const isOkDisabled = () => topicUrl.trim().length < 1;

  useEffect(() => {
    // If we previously had a matching topic but now do not, reset the name.
    if (hadMatchingTopicRef.current && !matchingTopic) {
      hadMatchingTopicRef.current = false;
      setTopicName('');
      return;
    }

    if (matchingTopic) {
      // If we have a matchingTopic but the current topicUrl doesn't match anymore, the user
      // has edited the URL after a match. Reset the name to empty.
      if (hadMatchingTopicRef.current && topicUrl !== matchingTopic.externalLink) {
        hadMatchingTopicRef.current = false;
        setTopicName('');
        return;
      }

      // If we have a matchingTopic and previously did not, set the name to the matchingTopic's name.
      if (!hadMatchingTopicRef.current) {
        hadMatchingTopicRef.current = true;
        setTopicName(matchingTopic.name || matchingTopic.externalLink);
      }
    }
  }, [matchingTopic, topicUrl]);

  const onTopicUrlInputBlur = useCallback(async () => {
    if (topicUrl) {
      try {
        const url = `${companyUrl}/topic_by_url/${encodeURIComponent(topicUrl)}`;

        const {topic: fetchedMatchingTopic} = await fetchApiAsync(ApiSearchTopic, url);

        if (!fetchedMatchingTopic) {
          setMatchingTopic(undefined);
          return;
        }

        setMatchingTopic(fetchedMatchingTopic);
      } catch (error) {
        if (error instanceof HttpError) {
          return;
        }

        reportToBugsnag(error); // Report non-HTTP errors to Bugsnag.
      }
    }
  }, [companyUrl, topicUrl]);

  /*
   * Render.
   */

  return (
    <OkCancelDialog
      title={
        topicToEdit ? (
          <Trans id="topic-web-title-edit">Edit link</Trans>
        ) : (
          <Trans id="topic-web-title-add">Add link</Trans>
        )
      }
      okButtonLabel={
        topicToEdit ? <Trans id="topic-web-ok-edit">Save</Trans> : <Trans id="topic-web-ok-add">Add</Trans>
      }
      onOkClick={onOkClick}
      onRequestClose={onRequestClose}
      isOkDisabled={isOkDisabled()}
    >
      <TextBox
        label={<Trans id="topic-web-url">External URL</Trans>}
        placeholder={localize(t('topic-web-add-url-placeholder')`Add a URL`)}
        value={topicUrl}
        onChange={setTopicUrl}
        onBlur={onTopicUrlInputBlur}
        shouldAutoFocus={!topicToEdit}
        noMargin
        isRequired
        isDisabled={Boolean(topicToEdit)}
      />
      <StyledExplanatoryTextDiv>
        <Trans id="topic-web-url-explanation">
          URL for a task, order, shipment, record etc. in an external system. e.g.
          &quot;https://example.com/ORD-1234&quot;.
        </Trans>
      </StyledExplanatoryTextDiv>
      <TextBox
        label={<Trans id="topic-web-name">Name</Trans>}
        placeholder={localize(t('topic-web-add-name-placeholder')`Add a name`)}
        value={topicName}
        noMargin
        onChange={setTopicName}
        shouldAutoFocus={Boolean(topicToEdit)}
        isDisabled={Boolean(matchingTopic && matchingTopic.externalLink === topicUrl)}
      />
      <StyledExplanatoryTextDiv>
        <Trans id="topic-web-name-explanation">A display name for the Link. e.g. &quot;ORD-1234&quot;</Trans>
      </StyledExplanatoryTextDiv>
    </OkCancelDialog>
  );
};
